import { supabase } from './supabase';
import { Job, Candidate, JobApplication } from '../types/jobs';

// Fetch all active jobs
export const fetchJobs = async (): Promise<Job[]> => {
  const { data, error } = await supabase
    .from('jobs')
    .select('*')
    .eq('is_active', true)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching jobs:', error);
    throw new Error('Failed to fetch jobs');
  }

  return data || [];
};

// Fetch a single job by ID
export const fetchJobById = async (id: string): Promise<Job | null> => {
  const { data, error } = await supabase
    .from('jobs')
    .select('*')
    .eq('id', id)
    .eq('is_active', true)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      // No rows returned
      return null;
    }

    console.error('Error fetching job:', error);
    throw new Error('Failed to fetch job');
  }

  return data;
};

// Fetch a single job by slug
export const fetchJobBySlug = async (slug: string): Promise<Job | null> => {
  const { data, error } = await supabase
    .from('jobs')
    .select('*')
    .eq('slug', slug)
    .eq('is_active', true)
    .single();

  if (error) {
    if (error.code === 'PGRST116') {
      // No rows returned
      return null;
    }

    console.error('Error fetching job by slug:', error);
    throw new Error('Failed to fetch job');
  }

  return data;
};

// Submit job application via Edge Function with service role
export const submitJobApplication = async (
  jobId: string,
  application: JobApplication
): Promise<{ success: boolean; candidateId?: string }> => {
  try {
    // Convert resume file to base64
    if (!application.resume) {
      throw new Error('Resume is required');
    }

    const resumeBase64 = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();

      reader.onloadend = () => {
        const base64 = reader.result as string;

        // Remove data URL prefix (e.g., "data:application/pdf;base64,")
        const base64Data = base64.split(',')[1];

        resolve(base64Data);
      };

      reader.onerror = reject;

      reader.readAsDataURL(application.resume!);
    });

    // Call Edge Function with service role
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    const functionUrl = `${supabaseUrl}/functions/v1/submit-application`;

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${supabaseAnonKey}`,
      },
      body: JSON.stringify({
        jobId,
        firstName: application.firstName,
        lastName: application.lastName,
        email: application.email,
        phone: application.phone,
        countryCode: application.countryCode,
        resumeData: resumeBase64,
        resumeName: application.resume.name,
      }),
    });

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({ error: 'Unknown error' }));

      throw new Error(
        errorData.error || 'Failed to submit application'
      );
    }

    const result = await response.json();

    return {
      success: true,
      candidateId: result.candidateId,
    };
  } catch (error) {
    console.error('Application submission error:', error);
    throw error;
  }
};

// Fetch candidates for a job (admin only)
export const fetchCandidatesForJob = async (
  jobId: string
): Promise<Candidate[]> => {
  const { data, error } = await supabase
    .from('candidates')
    .select('*')
    .eq('job_id', jobId)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('Error fetching candidates:', error);
    throw new Error('Failed to fetch candidates');
  }

  return data || [];
};

// Update candidate status (admin only)
export const updateCandidateStatus = async (
  candidateId: string,
  status: Candidate['status']
): Promise<void> => {
  const { error } = await supabase
    .from('candidates')
    .update({ status })
    .eq('id', candidateId);

  if (error) {
    console.error('Error updating candidate status:', error);
    throw new Error('Failed to update candidate status');
  }
};

// Get job categories for filtering
export const getJobCategories = async (): Promise<string[]> => {
  const { data, error } = await supabase
    .from('jobs')
    .select('category')
    .eq('is_active', true);

  if (error) {
    console.error('Error fetching categories:', error);
    return [];
  }

  const categories = [...new Set(data.map(job => job.category))];

  return categories.sort();
};

// Format salary range
export const formatSalaryRange = (
  min?: number,
  max?: number
): string | null => {
  if (!min && !max) return null;

  if (min && max) {
    return `AED ${min.toLocaleString()} - ${max.toLocaleString()}`;
  }

  if (min) {
    return `AED ${min.toLocaleString()}+`;
  }

  if (max) {
    return `Up to AED ${max.toLocaleString()}`;
  }

  return null;
};

// Format experience requirement
export const formatExperience = (years: number): string => {
  if (years === 0) return 'Entry level';
  if (years === 1) return '1 year';

  return `${years}+ years`;
};